import React, { PureComponent } from 'react';
import { Link } from 'react-router';
import PropTypes from 'prop-types';
import MomentPropTypes from 'react-moment-proptypes';
import VERTICALS from 'shared/lib/constants/verticals';
import IconCircle from 'shared/lib/components/IconCircle';
import capitalize from 'lodash/capitalize';
import compact from 'lodash/compact';
import map from 'lodash/map';
import moment from 'moment-timezone';
import classnames from 'classnames';
import './PolicyRow.css';

const propTypes = {
  createdAt: MomentPropTypes.momentString,
  verticalCode: PropTypes.string,
  underwriter: PropTypes.string,
  details: PropTypes.object,
  icon: PropTypes.string,
  priceDesc: PropTypes.string,
  status: PropTypes.oneOf([
    'approved',
    'pending',
    'pending_change',
    'canceled',
    'rejected',
  ]),
  withBadge: PropTypes.bool,
  isHeader: PropTypes.bool,
};

const defaultProps = {
  icon: null,
  priceDesc: null,
  withBadge: false,
  isHeader: false,
};

class PolicyRow extends PureComponent {
  get heading() {
    const verticalInfo = VERTICALS[this.props.verticalCode] || {};
    let heading = `${capitalize(verticalInfo.title)} policy`;
    switch (this.props.status) {
      case 'pending':
      case 'pending_change':
        heading += ' (pending)';
        break;
      case 'canceled':
        heading += ' (canceled)';
        break;
      default:
        break;
    }
    return heading;
  }

  get detailNode() {
    const { details } = this.props;
    let { address } = details;
    switch (this.props.verticalCode) {
      case 'renters':
        return compact([
          address.line1,
          address.line2,
          `${address.city} ${address.region}`,
          address.postal,
        ]).join(', ');
      case 'landlord':
        return compact([
          address.line1,
          address.line2,
          `${address.city} ${address.region}`,
          address.postal,
        ]).join(', ');
      case 'baggage':
        let purchaseDate = moment(this.props.createdAt).format('M/D/YYYY');
        return `${details.airline_name} (${
          details.airline_code
        }) on ${purchaseDate}`;
      case 'devices':
        return details.modal_name;
      case 'pet':
        return (details.pet_names || map(details.pets, 'name')).join(', ');
      case 'rental_car':
        let pickUpDate = moment(details.rental_started, 'YYYY-MM-DD').format(
          'MMM DD, YYYY'
        );
        let returnDate = moment(details.rental_ended, 'YYYY-MM-DD').format(
          'MMM DD, YYYY'
        );
        return `${details.rental_company} from ${pickUpDate} to ${returnDate}`;
      case 'taxi':
        let startDateTime = moment(details.coverage_start_date).format(
          'M/D/YYYY h:mm a'
        );
        let rideNoun = details.ride_count > 1 ? 'rides' : 'ride';
        return `${startDateTime}, ${details.ride_count} ${rideNoun}`;
      case 'collectibles':
        return details.categories.join(', ');
      case 'commercial':
        return details.business_name;
      default:
        return null;
    }
  }

  get iconNode() {
    if (this.props.icon) {
      const baseProps = {
        className: 'PolicyRow-icon',
        icon: this.props.icon,
        size: 60,
        background: this.props.status === 'canceled' ? 'gray' : 'blue',
        color: this.props.status === 'canceled' ? 'medium' : 'dark',
      };

      let colorProps;
      switch (this.props.status) {
        case 'approved':
          colorProps = {
            background: 'blue',
            color: 'dark',
          };
          break;
        case 'pending':
        case 'pending_change':
          colorProps = {
            background: 'gray',
            color: 'medium',
          };
          break;
        case 'canceled':
          colorProps = {
            background: 'gray',
            color: 'medium',
          };
          break;
        default:
          colorProps = {
            background: 'blue',
            color: 'dark',
          };
          break;
      }

      let badgeProps;
      switch (this.props.status) {
        case 'approved':
          badgeProps = {
            badgeIcon: 'check',
            badgeBackground: 'green',
            badgeColor: 'light',
          };
          break;
        case 'pending':
        case 'pending_change':
          badgeProps = {
            badgeIcon: 'access_time',
            badgeBackground: 'blue',
            badgeColor: 'dark',
          };
          break;
        case 'canceled':
        case 'rejected':
          badgeProps = {
            badgeIcon: 'close',
            badgeBackground: 'red',
            badgeColor: 'light',
          };
          break;
        default:
          badgeProps = {};
          break;
      }

      const iconProps = {
        ...baseProps,
        ...colorProps,
      };

      if (this.props.withBadge) {
        Object.assign(iconProps, badgeProps);
      }

      return <IconCircle {...iconProps} />;
    } else {
      return null;
    }
  }

  render() {
    const {
      isHeader,
      policyId,
      priceDesc,
      underwriter,
      verticalCode,
    } = this.props;

    return (
      <Link
        className={classnames('PolicyRow', {
          'PolicyRow--header': isHeader,
        })}
        to={`/account/policies/${verticalCode}/${policyId}`}
      >
        {this.iconNode}
        <div className="PolicyRow-info">
          <h4 className="PolicyRow-heading">{this.heading}</h4>
          <p className="PolicyRow-detail">{this.detailNode}</p>
          <p className="PolicyRow-underwriter">via {underwriter}</p>
        </div>
        {priceDesc && <div className="PolicyRow-priceDesc">{priceDesc}</div>}
      </Link>
    );
  }
}

PolicyRow.propTypes = propTypes;
PolicyRow.defaultProps = defaultProps;

export default PolicyRow;
